/**
 * SearchView Component
 * User search and discovery interface
 */

import { UserListItem } from '@components/UserListItem';
import { EmptyState, LoadingSpinner } from '@components/ui';
import { UserProfileModal } from '@components/ui/UserProfileModal';
import { useUserSearch } from '@hooks/useUserSearch';
import { Search, Users, X } from 'lucide-react';
import { type ReactElement, useEffect, useMemo, useState } from 'react';

const DEBOUNCE_MS = 500;
const MIN_QUERY_LENGTH = 2;

// Debounce utility
// biome-ignore lint/suspicious/noExplicitAny: Generic function constraint requires `any` for proper type inference with Parameters<T>
function debounce<T extends (...args: any[]) => void>(
  func: T,
  wait: number,
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

export function SearchView(): ReactElement {
  const [query, setQuery] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const { results, loading, error, search, clear } = useUserSearch();

  // Debounced search function
  const debouncedSearch = useMemo(
    () =>
      debounce((q: string) => {
        if (q.trim().length >= MIN_QUERY_LENGTH) {
          search(q);
        } else {
          clear();
        }
      }, DEBOUNCE_MS),
    [search, clear],
  );

  // Trigger search when query changes
  useEffect(() => {
    debouncedSearch(query);
  }, [query, debouncedSearch]);

  return (
    <div data-testid="search-view" className="max-w-5xl mx-auto px-4 py-8 space-y-6">
      {/* Header */}
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-bold text-gray-100">Discover Users</h1>
        <p className="text-theme-text-secondary">
          Find other bettors to follow and learn from their picks
        </p>
      </div>

      {/* Search Input */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="w-5 h-5 text-theme-text-secondary" />
        </div>
        <input
          type="text"
          data-testid="user-search-input"
          placeholder="Search by username or name..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-3 rounded-lg bg-theme-surface border border-theme-border text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        {query && (
          <button
            type="button"
            onClick={() => {
              setQuery('');
              clear();
            }}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-theme-text-secondary hover:text-gray-200"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-theme-danger/10 border border-theme-danger/20 rounded-lg p-4 text-center">
          <p className="text-theme-danger">{error}</p>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
        </div>
      )}

      {/* Results List */}
      {!loading && results.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm text-theme-text-secondary">
            {results.length} {results.length === 1 ? 'user' : 'users'} found
          </p>
          <div className="bg-theme-surface/50 rounded-lg border border-theme-border divide-y divide-gray-700">
            {results.map((user) => (
              <UserListItem
                key={user.id}
                user={user}
                onViewProfile={() => setSelectedUserId(user.id)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Empty State - No Results */}
      {!loading && query.trim().length >= MIN_QUERY_LENGTH && results.length === 0 && (
        <EmptyState
          icon={<Search className="w-16 h-16 text-gray-600" />}
          title="No users found"
          description={`No users match "${query}". Try a different search term.`}
        />
      )}

      {/* Empty State - Start Searching */}
      {!loading && query.trim().length < MIN_QUERY_LENGTH && results.length === 0 && (
        <div className="rebrand-empty-state p-8 sm:p-12 text-center max-w-lg mx-auto">
          <div className="w-16 h-16 rounded-2xl bg-theme-primary/10 flex items-center justify-center mx-auto mb-5">
            <Users className="w-8 h-8 text-theme-primary" />
          </div>
          <h3 className="text-xl font-bold text-white mb-2">Discover bettors</h3>
          <p className="text-theme-text-tertiary mb-6">
            Search by username or name to find other bettors, follow their activity, and learn from
            their picks.
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            {['Top performers', 'Sharp bettors', 'Active users'].map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 rounded-full bg-theme-surface border border-theme-border/50 text-xs text-theme-text-tertiary"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Profile Modal */}
      {selectedUserId && (
        <UserProfileModal userId={selectedUserId} onClose={() => setSelectedUserId(null)} />
      )}
    </div>
  );
}
